import { useCallback, useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import { ArrowsCounterClockwiseIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import Beacon from "@gx/dls/beacons/Beacon";
import Button, { ButtonSize } from "@gx/dls/buttons/Button";
import FlexWrapper, { AlignItems } from "@gx/dls/containers/FlexWrapper";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import { SourceSyncStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import { GetSourceRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";
import { ListSourceSyncsRequestSchema } from "@gx/ts-contracts/services/source/api/source_sync_pb";

import BaseHeader from "@/layouts/components/BaseHeader";

import {
  RESYNCABLE_SOURCE_TYPES,
  SYNC_STATUS_TO_BUTTON_DISABLED_MAP,
} from "@/pages/sources/components/edit/constants";
import { useEditSourceFormContext } from "@/pages/sources/components/edit/hooks";
import { EditSourceDrawerTab } from "@/pages/sources/components/edit/types";
import { shouldRefetchSourceSyncs } from "@/pages/sources/components/edit/utils";
import SourceIcon from "@/pages/sources/components/SourceIcon";
import {
  SYNC_STATUS_TO_BEACON_VARIANT_MAP,
  SYNC_STATUS_TO_TEXT_MAP,
  SYNC_STATUS_TO_TEXT_VARIANT_MAP,
} from "@/pages/sources/constants";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useCreateSyncMutation } from "@/api/mutations/syncs";
import { useGetSourceQuery } from "@/api/queries/sources";
import { useListSourceSyncsQuery } from "@/api/queries/syncs";

import { convertProtobufTimestampToDate, formatDateTime } from "@/utils/datetimes";

interface EditSourceDrawerHeaderProps {
  onClose?: () => void;
}

const EditSourceDrawerHeader = ({ onClose }: EditSourceDrawerHeaderProps) => {
  const { state } = useEditSourceFormContext();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { data: sourceData } = useGetSourceQuery({
    input: create(GetSourceRequestSchema, { sourceId: state.source.id }),
  });

  const { data: syncsData } = useListSourceSyncsQuery({
    input: create(ListSourceSyncsRequestSchema, {
      sourceId: state.source.id,
    }),
    options: {
      refetchInterval: (query) => shouldRefetchSourceSyncs(2000, query.state.data?.syncs),
    },
  });

  const latestSyncStatus = useMemo(() => {
    if (!syncsData?.syncs || syncsData.syncs.length === 0) {
      return SourceSyncStatus.UNSPECIFIED;
    }
    return syncsData.syncs[syncsData.syncs.length - 1].syncStatus;
  }, [syncsData?.syncs]);

  const { mutate: createSourceSync } = useCreateSyncMutation({
    onSuccess: () => {
      showToast({
        variant: ToastVariant.SUCCESS,
        header: "Sync started",
        subheader: "The sync has been started successfully",
      });
    },
    onError: () => {
      showToast({
        variant: ToastVariant.ERROR,
        header: "Failed to start sync",
        subheader: "Failed to start the sync. Please try again.",
      });
    },
  });

  const handleSyncClick = useCallback(() => {
    createSourceSync({ sourceId: state.source.id });
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, sourceTab: EditSourceDrawerTab.SYNCS }),
    });
  }, [createSourceSync, navigate, state.source.id]);

  const actions = useMemo(() => {
    return RESYNCABLE_SOURCE_TYPES.includes(state.source.sourceType ?? SourceType.UNSPECIFIED)
      ? [
          <Button
            key="sync"
            label="Sync"
            icon={ArrowsCounterClockwiseIcon}
            size={ButtonSize.SMALL}
            onClick={handleSyncClick}
            isDisabled={SYNC_STATUS_TO_BUTTON_DISABLED_MAP[latestSyncStatus]}
          />,
        ]
      : [];
  }, [handleSyncClick, latestSyncStatus, state.source.sourceType]);

  const updatedAtFormatted = useMemo(() => {
    const updatedAt = sourceData?.source?.updatedAt ?? state.source.updatedAt;
    if (!updatedAt) return "—";
    const date = convertProtobufTimestampToDate(updatedAt);
    return formatDateTime(date.toISOString());
  }, [sourceData?.source?.updatedAt, state.source.updatedAt]);

  const statusDescription = (
    <FlexWrapper gap={8} alignItems={AlignItems.CENTER}>
      <FlexWrapper gap={4} alignItems={AlignItems.CENTER}>
        <Beacon variant={SYNC_STATUS_TO_BEACON_VARIANT_MAP[latestSyncStatus]} />
        <Text
          size={TextSize.BODY_SM}
          weight={TextWeight.MEDIUM}
          variant={SYNC_STATUS_TO_TEXT_VARIANT_MAP[latestSyncStatus]}
        >
          {SYNC_STATUS_TO_TEXT_MAP[latestSyncStatus]}
        </Text>
      </FlexWrapper>
      <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
        Last synced on {updatedAtFormatted}
      </Text>
    </FlexWrapper>
  );

  return (
    <FlexWrapper alignItems={AlignItems.CENTER} gap={12} padding="12px 16px" fillWidth>
      <SourceIcon sourceType={state.source.sourceType ?? SourceType.UNSPECIFIED} />
      <BaseHeader
        title={state.source.name}
        description={statusDescription}
        actions={actions}
        onClose={onClose}
      />
    </FlexWrapper>
  );
};

export default EditSourceDrawerHeader;
